
import React, { useRef, useEffect } from 'react';
import { PRICING_TIERS } from '../constants';
import { PricingTier, Language } from '../types';
import { NoiseBackground } from './ui/noise-background';
import LightRays from './ui/LightRays';
import { translations } from '../translations';

interface PricingProps {
  lang: Language;
  onAddToCart: (tier: PricingTier) => void;
}

const Pricing: React.FC<PricingProps> = ({ lang, onAddToCart }) => {
  const gridRef = useRef<HTMLDivElement>(null);
  const glowColor = "14, 165, 233";
  const spotlightRadius = 350;
  const t = translations[lang].pricing;

  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;
    const handleMouseMove = (e: MouseEvent) => {
      const cards = grid.querySelectorAll('.pricing-card-item');
      cards.forEach((card: any) => {
        const rect = card.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        card.style.setProperty('--glow-x', `${x}%`);
        card.style.setProperty('--glow-y', `${y}%`);
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        const distance = Math.sqrt(Math.pow(e.clientX - centerX, 2) + Math.pow(e.clientY - centerY, 2));
        card.style.setProperty('--glow-intensity', distance < spotlightRadius * 1.5 ? '1' : '0');
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <section id="pricing" className="py-32 bg-zinc-950 relative overflow-hidden">
      <style>{`
        @keyframes vibrate { 0%, 2%, 4%, 6%, 8%, 10%, 100% { transform: translate(0, 0); } 1%, 3%, 5%, 7%, 9% { transform: translate(-1px, 1px); } 1.5%, 3.5%, 5.5%, 7.5%, 9.5% { transform: translate(1px, -1px); } }
        .animate-vibrate { animation: vibrate 3s infinite; }
        .pricing-card-item { --glow-x: 50%; --glow-y: 50%; --glow-intensity: 0; --glow-radius: ${spotlightRadius}px; position: relative; background: rgba(255, 255, 255, 0.02); border: 1px solid rgba(255, 255, 255, 0.05); cursor: default; }
        .pricing-card-item::after { content: ''; position: absolute; inset: 0; padding: 1px; background: radial-gradient(var(--glow-radius) circle at var(--glow-x) var(--glow-y), rgba(${glowColor}, calc(var(--glow-intensity) * 0.4)) 0%, transparent 70%); -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0); mask-composite: exclude; pointer-events: none; opacity: 0; transition: opacity 0.5s ease; border-radius: inherit; }
        .pricing-card-item:hover::after { opacity: 1; }
      `}</style>
      <div className="absolute inset-0 z-0 opacity-40">
        <LightRays raysOrigin="top-center" raysColor="#0ea5e9" raysSpeed={0.8} lightSpread={0.6} rayLength={3} followMouse={true} mouseInfluence={0.08} noiseAmount={0.02} distortion={0.05} pulsating={false} fadeDistance={1.2} saturation={1} />
      </div>
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center mb-24">
          <h2 className="text-5xl md:text-7xl font-bold mb-8 tracking-tighter text-white">{t.title}</h2>
          <p className="text-gray-400 text-xl md:text-2xl max-w-3xl mx-auto font-light leading-relaxed">{t.subtitle}</p>
        </div>
        <div ref={gridRef} className="grid md:grid-cols-3 gap-10">
          {PRICING_TIERS.map((tier) => {
            const isRecommended = tier.recommended;
            const tierInfo = (t.tiers as any)[tier.id];
            return (
              <div key={tier.id} className={`pricing-card-item relative rounded-[32px] transition-all duration-500 hover:-translate-y-2 ${isRecommended ? 'ring-2 ring-sky-500 scale-105 z-10 bg-sky-500/[0.04] border-sky-500/40 shadow-2xl shadow-sky-500/10' : 'hover:border-white/20'}`}>
                {isRecommended && <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[30] whitespace-nowrap"><div className="bg-gradient-to-r from-sky-600 via-sky-500 to-blue-600 text-white text-[11px] font-black uppercase tracking-[0.2em] px-8 py-2.5 rounded-full shadow-[0_4px_20px_rgba(14,165,233,0.4)] border border-white/20">{t.popular}</div></div>}
                <div className="relative h-full w-full overflow-hidden rounded-[32px] p-12 flex flex-col">
                  <div className="mb-10 relative z-10">
                    <h3 className="text-2xl font-bold mb-4 tracking-tight text-white">{tierInfo.name}</h3>
                    <div className="flex items-baseline gap-2"><span className="text-6xl font-black tracking-tighter text-white">{tier.price}€</span><span className="text-gray-500 text-lg">/ {t.period}</span></div>
                  </div>
                  <ul className="space-y-6 mb-12 flex-grow relative z-10">
                    {tierInfo.features.map((feature: string, i: number) => (
                      <li key={i} className="text-lg text-gray-400 flex items-start gap-4">
                        <div className="mt-1.5 shrink-0"><svg className={`w-5 h-5 ${isRecommended ? 'text-sky-400' : 'text-sky-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg></div>
                        <span className="leading-tight">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="relative z-10">
                    <NoiseBackground containerClassName="w-full p-[2px] rounded-full" gradientColors={isRecommended ? ["rgb(14, 165, 233)", "rgb(34, 211, 238)", "rgb(2, 132, 199)"] : ["rgb(255, 100, 150)", "rgb(100, 150, 255)", "rgb(255, 200, 100)"]}>
                      <div role="button" onClick={() => onAddToCart(tier)} className={`flex items-center justify-center h-full w-full rounded-full py-5 font-bold cursor-pointer transition-all duration-100 active:scale-95 text-center text-lg uppercase tracking-tight ${isRecommended ? "bg-gradient-to-r from-sky-600 via-sky-500 to-blue-600 text-white shadow-[0px_2px_0px_0px_rgba(255,255,255,0.2)_inset] animate-vibrate" : "bg-gradient-to-r from-neutral-100 via-neutral-100 to-white text-black shadow-[0px_2px_0px_0px_rgba(255,255,255,0.5)_inset]"}`}>
                        {t.buy}
                      </div>
                    </NoiseBackground>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Pricing;
